/**
 * `/quote/playbooks` — Playbook management (M-QUOTE.3.b).
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCreateQuotePlaybook,
  useDeleteQuotePlaybook,
  useQuotePlaybooks,
} from "@/hooks/use-quote";

export function QuotePlaybooksPage() {
  const list = useQuotePlaybooks();
  const createMut = useCreateQuotePlaybook();
  const deleteMut = useDeleteQuotePlaybook();

  const [vertical, setVertical] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!vertical.trim() || !name.trim()) return;
    createMut.mutate(
      {
        vertical: vertical.trim(),
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        prompts: {},
      },
      {
        onSuccess: () => {
          setVertical("");
          setName("");
          setDescription("");
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Quote</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Playbooks</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Per-vertical prompt overrides that customize AI responses to your industry's tone and
          terminology.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Add playbook</CardTitle>
          <CardDescription>
            Vertical is a short tag (HVAC, plumbing, lawn care, etc.). Prompt overrides can be added
            later from the detail view.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Vertical (e.g. hvac)"
              value={vertical}
              onChange={(e) => setVertical(e.target.value)}
              required
            />
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm md:col-span-2"
              placeholder="Playbook name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm md:col-span-3"
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="md:col-span-3">
              <Button type="submit" disabled={createMut.isPending}>
                {createMut.isPending ? "Adding…" : "Add playbook"}
              </Button>
              {createMut.error && (
                <span className="ml-3 text-sm text-destructive">{createMut.error.message}</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {list.isLoading && <Skeleton className="h-16 w-full" />}
      {list.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              Couldn't load playbooks: {list.error.message}
            </p>
          </CardContent>
        </Card>
      )}
      {list.data && list.data.items.length === 0 && !list.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>No playbooks yet</CardTitle>
            <CardDescription>
              Start with one for your primary vertical; you can add more as you specialize.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {list.data && list.data.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {list.data.items.map((pb) => (
            <li key={pb.id}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{pb.name}</CardTitle>
                  <CardDescription>
                    <span className="font-mono text-xs uppercase tracking-wider">
                      {pb.vertical}
                    </span>
                    {pb.description && <> · {pb.description}</>}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-between text-sm text-muted-foreground">
                  <span>{Object.keys(pb.prompts).length} prompt override(s)</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteMut.mutate(pb.id)}
                    disabled={deleteMut.isPending}
                  >
                    Delete
                  </Button>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
